// Removed import from @shared/documents
import ReactMarkdown from 'react-markdown';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import useValidatedParams from '../../hooks/useValidatedParams';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
	PageTitle,
} from '../Shared/CommonStyles'; // Import shared styles

import * as S from './Post.styles';

// Define the types locally
interface PostElementDoc {
	type: string;
	content: string;
}

interface PostDocument {
	_id: string;
	title: string;
	elements: PostElementDoc[];
	data_publicacio: Date | string;
	data_actualitzacio: Date | string;
	unitat?: string;
	url: string;
	data_publicacio_dd_mm_yyyy?: string;
	data_actualitzacio_dd_mm_yyyy?: string;
	preview_image: number;
}

/**
 * Extract YouTube video ID from various YouTube URL formats.
 */
const extractYouTubeId = (url: string): string | null => {
	const patterns = [
		/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
		/^([a-zA-Z0-9_-]{11})$/, // bare video ID
	];
	for (const pattern of patterns) {
		const match = url.match(pattern);
		if (match) return match[1];
	}
	return null;
};

const Post = () => {
	const { user } = useAuth();

	const {
		params: { id },
		paramsError,
	} = useValidatedParams({ paramNames: ['id'], errorMessage: 'Id no vàlid' });

	const {
		data: post,
		isLoading: isFetchingPost,
		error: fetchPostError,
	} = useFetch<PostDocument>({
		queryKey: ['post', id],
		url: `/api/posts/${id}`,
	});

	const isAuthenticated =
		!!user &&
		!!post &&
		(user.roles.includes('Admin') ||
			user.roles.includes('Agrupament') ||
			user.roles.includes('Caps') || // trusted curators: any post
			user.unitat === post.unitat);

	const deleteMutation = useSimpleMutation<undefined>({
		method: 'delete',
		url: `/api/posts/${id}`,
		dependencies: [id],
		queriesToInvalidate: [
			['post', id],
			post && post.unitat
				? ['posts', post.unitat]
				: ['posts', 'agrupament'],
			// Standalone posts list bucket ('tots') — see PostForm note.
			['posts', 'tots'],
		],
		navigateUrl: '/posts',
	});

	const handleDelete = async () => {
		if (window.confirm('Segur que vols eliminar el post?')) {
			deleteMutation.mutate(undefined);
		}
	};

	const loading = isFetchingPost || deleteMutation.isPending;
	const error = paramsError || fetchPostError || deleteMutation.error;

	if (loading) {
		return (
			<LoadingMessage>
				<LoadingGif size="large" />
			</LoadingMessage>
		);
	}

	if (error) {
		return <ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>;
	}

	if (!post) {
		return <ErrorMessage>No s&apos;ha pogut carregar el post</ErrorMessage>;
	}

	return post ? (
		<S.PostSpecificContainer>
			<PageTitle>{post.title}</PageTitle>
			{/* <PostMeta>Published: {post.data_publicacio_dd_mm_yyyy}</PostMeta> */}
			<S.PostContent>
				{post.elements.map((element, index) => {
					switch (element.type) {
					case 'paragraph':
						return (
							<ReactMarkdown key={index}>
								{element.content}
							</ReactMarkdown>
						);
					case 'image':
						return (
							<S.StyledImage
								key={index}
								src={element.content}
								alt={`Imatge ${index + 1} del post`}
							/>
						);
					case 'video':
						const videoId = extractYouTubeId(element.content);
						if (!videoId) return null;
						return (
							<S.VideoContainer key={index}>
								<iframe
									src={`https://www.youtube.com/embed/${videoId}`}
									title="YouTube video player"
									allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
									allowFullScreen
								/>
							</S.VideoContainer>
						);
					case 'video_self':
						return (
							<S.VideoPlayer
								key={index}
								src={element.content}
								controls
								preload="metadata"
							/>
						);
					default:
						return null;
					}
				})}
			</S.PostContent>

			{isAuthenticated && (
				<S.AdminActions>
					<S.EditLink to={`/posts/${id}/editar`}>Editar</S.EditLink>
					<S.DeleteButton onClick={handleDelete}>
						Eliminar
					</S.DeleteButton>
				</S.AdminActions>
			)}
		</S.PostSpecificContainer>
	) : (
		<ErrorMessage>No s&apos;ha pogut carregar el post</ErrorMessage>
	);
};

export default Post;
